import React from "react";
import { Link } from "react-router-dom";

/* ---- Brand assets (from the Intrinsic Daily brand book) ---- */
export const LOGO_PRIMARY = "https://customer-assets-agu9un31.emergentagent.net/job_formation-dashboard/artifacts/s63a2n7t_Intrinsic_Daily_Primary_Logo.png";
export const LOGO_MONOGRAM = "https://customer-assets-agu9un31.emergentagent.net/job_formation-dashboard/artifacts/o5lfjpbs_Intrinsic_Daily_Monogram.png";

export const TAGLINE = "Grow with intention. Live each day on purpose.";
export const SIGNATURE_QUESTION = "Who are you becoming — and what needs your best energy today?";

/* Primary lockup — dark wordmark, best on ivory/white */
export const Logo = ({ className = "h-11" }) => (
  <img src={LOGO_PRIMARY} alt="Intrinsic Daily" className={`${className} w-auto object-contain`} />
);

/* Monogram only — for compact / dark surfaces */
export const Monogram = ({ className = "h-10" }) => (
  <img src={LOGO_MONOGRAM} alt="Intrinsic Daily" className={`${className} w-auto object-contain`} />
);

/* ---- Reusable brand UI atoms ---- */
export const IDButton = ({ as = "link", to = "#", onClick, children, variant = "solid", className = "", type, disabled, testid }) => {
  const base = "inline-flex items-center justify-center gap-2 id-sans text-[13px] font-semibold tracking-[0.02em] rounded-full px-6 py-3 transition-colors duration-200 disabled:opacity-60 disabled:pointer-events-none";
  const styles = {
    solid: "bg-[var(--id-charcoal)] text-[var(--id-ivory)] hover:bg-[var(--id-walnut)]",
    gold: "bg-[var(--id-gold)] text-white hover:brightness-95",
    ghost: "border border-[var(--id-charcoal)]/25 text-[var(--id-charcoal)] hover:bg-[var(--id-charcoal)]/5",
    ghostLight: "border border-white/30 text-white hover:bg-white/10",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  if (as === "button") return <button type={type || "button"} onClick={onClick} disabled={disabled} data-testid={testid} className={cls}>{children}</button>;
  return <Link to={to} onClick={onClick} data-testid={testid} className={cls}>{children}</Link>;
};

export const SectionLabel = ({ children, className = "" }) => (
  <p className={`id-sans text-[11px] font-semibold uppercase tracking-[0.28em] text-[var(--id-gold)] ${className}`}>{children}</p>
);

export const Eyebrow = SectionLabel;

/* ---- Content data used across pages ---- */
export const PILLARS = [
  { key: "intentionality", name: "Intentionality", line: "Make purpose visible before action.",
    detail: "Every day begins with why. We surface your purpose first, so your energy flows toward what actually matters — not just what's loud." },
  { key: "integration", name: "Integration", line: "Treat life as one connected system.",
    detail: "Faith, health, work, wealth, and relationships aren't separate to-do lists. Intrinsic Daily connects them into a single, coherent operating rhythm." },
  { key: "stewardship", name: "Stewardship", line: "Manage your resources with care.",
    detail: "Time, energy, health, gifts, and relationships are resources to steward well. We help you allocate them like the leader of your own life." },
  { key: "elegance", name: "Elegance", line: "Reduce noise. Elevate the essential.",
    detail: "Beauty should support clarity. A calm, uncluttered interface keeps your attention on the few things that move your life forward." },
  { key: "becoming", name: "Becoming", line: "Celebrate progress; keep the future self in view.",
    detail: "Growth is the point. We honor your progress while keeping the woman you're becoming clearly in focus — every single day." },
];

export const PERSONAS = [
  { key: "executive", name: "The Executive Builder",
    line: "Leading teams, growing influence, building a life that matches your capacity.",
    detail: "You carry real responsibility and refuse to let your inner life fall behind your outer success. Intrinsic Daily gives you an executive suite for your whole life." },
  { key: "intentional", name: "The Intentional Woman",
    line: "Seeking deeper alignment across faith, wellness, relationships, work, and growth.",
    detail: "You're capable and full — and you want a system that respects both your aspirations and your humanity. This is a calm home for all of it." },
  { key: "emerging", name: "The Emerging Leader",
    line: "Ready to replace reactive living with disciplined routines and measurable progress.",
    detail: "You're stepping into more. Intrinsic Daily helps you trade scattered effort for reflection, rhythm, and visible progress you can trust." },
];

export const FOCUS_AREAS = [
  "Faith & Character", "Health & Vitality", "Executive & Career",
  "Wealth & Ownership", "Relationships & Legacy", "Personal Growth",
  "Rest & Wellness", "Home & Environment",
];

export const PERSONA_OPTIONS = PERSONAS.map((p) => p.name);
